import { captureException, Scope } from "@sentry/vue";
import jwtDecode from "jwt-decode";
import { HTTPError } from "ky";
import { noop } from "lodash-es";
import baseSlugify from "slugify";

import { FileListFile } from "./components/ui/FileList.vue";
import {
  AuditReport,
  AuditStatus,
  AuditType,
  AuditTypeString,
  CriterionResultUserImpact,
  CriteriumResultStatus
} from "./types";

const formatter = new Intl.DateTimeFormat("fr-FR", {
  year: "numeric",
  month: "long",
  day: "numeric"
});

const abridgedFormatter = new Intl.DateTimeFormat("fr-FR", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit"
});

/**
 * Format a date string into a readable date ("17 août 2022")
 * @param {string} dateString
 * @param {boolean} short - abridged version: "17/08/2022"
 */
export function formatDate(dateString: string, short?: boolean): string {
  return short
    ? abridgedFormatter.format(new Date(dateString))
    : formatter.format(new Date(dateString));
}

/**
 * Check if 2 dates are the same day, ignoring time
 */
export function isSameDay(dateString1: string, dateString2: string): boolean {
  const date1 = new Date(dateString1).toDateString();
  const date2 = new Date(dateString2).toDateString();

  return date1 === date2;
}

const FORMATTED_USER_IMPACT = {
  [CriterionResultUserImpact.MINOR]: "mineur",
  [CriterionResultUserImpact.MAJOR]: "majeur",
  [CriterionResultUserImpact.BLOCKING]: "bloquant"
};

/**
 * Format a criterion result user impact type string into French.
 */
export function formatUserImpact(
  userImpact: CriterionResultUserImpact | `${CriterionResultUserImpact}`
): string {
  return FORMATTED_USER_IMPACT[userImpact];
}

const FORMATTED_STATUS = {
  [CriteriumResultStatus.NOT_TESTED]: "Non testé",
  [CriteriumResultStatus.COMPLIANT]: "Conforme",
  [CriteriumResultStatus.NOT_COMPLIANT]: "Non conforme",
  [CriteriumResultStatus.NOT_APPLICABLE]: "Non applicable"
};

/**
 * Format a criterion result status type string into French.
 */
export function formatStatus(
  status: CriteriumResultStatus | `${CriteriumResultStatus}`
): string {
  return FORMATTED_STATUS[status];
}

const CRITERIA_COUNT = {
  [AuditType.FAST]: 25,
  [AuditType.COMPLEMENTARY]: 50,
  [AuditType.FULL]: 106
};

/**
 * Return the number of criteria for a given audit type.
 */
export function getCriteriaCount(auditType: AuditTypeString): number {
  return CRITERIA_COUNT[auditType];
}

/**
 * Return the audit status based on:
 * - the number of results excluding transverse (criteria count * number of pages)
 * - the status of each criteria
 * - the completion of a11y statement
 */
export function getAuditStatus(report: AuditReport): string {
  const transversePageId = report.context.samples[0].id;

  if (
    report.results.filter((r) => r.pageId !== transversePageId).length !==
      getCriteriaCount(report.auditType) *
        (report.context.samples.length - 1) ||
    report?.results
      .filter((r) => r.pageId !== transversePageId)
      .some((r) => r.status === CriteriumResultStatus.NOT_TESTED)
  ) {
    return AuditStatus.IN_PROGRESS;
  }

  return AuditStatus.COMPLETED;
}

export function getInnerWidth(element: HTMLElement): number {
  const widthWithPaddings = element.clientWidth;
  const elementComputedStyle = window.getComputedStyle(element, null);
  return (
    widthWithPaddings -
    parseFloat(elementComputedStyle.paddingLeft) -
    parseFloat(elementComputedStyle.paddingRight)
  );
}

export function slugify(value: string): string {
  return baseSlugify(value, { strict: true, lower: true });
}

export function formatBytes(bytes: number, decimals = 0) {
  if (!+bytes) return "0 octet";

  const k = 1000;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["octets", "Ko", "Mo", "Go", "To", "Po", "Eo", "Zo", "Yo"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

/**
 * Report an error to Sentry. If the error originates from a network request,
 * the request and response payloads are added to the error context.
 *
 * @param error Error to be captured
 * @param logRequestPayload Should the request payload be added to the error
 *   context. Set to `false` when capturing authentication requests.
 */
export async function captureWithPayloads(
  error: unknown,
  logRequestPayload = true
) {
  const scope = new Scope();

  if (error instanceof HTTPError) {
    const payloads: Record<string, string> = {};

    if (logRequestPayload) {
      await error.request
        .text()
        .then((data) => {
          payloads["Request Raw"] = data;
          try {
            payloads["Request JSON"] = JSON.stringify(
              JSON.parse(data),
              null,
              2
            );
          } catch {
            // noop, we dont do anything if it's not JSON
          }
        })
        .catch(noop);
    }

    await error.response
      .text()
      .then((data) => {
        payloads["Response Raw"] = data;
        try {
          payloads["Response JSON"] = JSON.stringify(JSON.parse(data), null, 2);
        } catch {
          // noop, we dont do anything if it's not JSON
        }
      })
      .catch(noop);

    scope.setContext("Network payloads", payloads);
  }

  captureException(error, scope);
}

export const pluralize = (singular: string, plural: string, count: number) =>
  count > 1 ? plural : singular;

// From https://emailregex.com/
// regexr.com/7lkj4
const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export function validateEmail(s: string): boolean {
  return !!s.match(EMAIL_REGEX);
}

// Trim + lowercase email
export function formatEmail(s: string): string {
  return s.trim().toLocaleLowerCase();
}

// https://regexr.com/819do
export const URL_REGEX = /^https?:\/\/(.*)/;

export function isJwtExpired(jwt: string) {
  const payload = jwtDecode<{ exp?: number }>(jwt);

  if (!payload.exp) {
    return false;
  }

  return Date.now() > payload.exp * 1000;
}

export function getUploadUrl(key: string): string {
  return `/uploads/${key}`;
}

/** Check if a tiptap document string corresponds to an empty document. */
export function isTiptapDocumentEmpty(
  jsonString: string | null | undefined
): boolean {
  if (!jsonString?.trim()) return true;

  try {
    JSON.parse(jsonString);
  } catch {
    // not json, most likely markdown
    return false;
  }

  const containsImage = jsonString.includes("\"type\":\"image\"");
  const containsText = jsonString
    .matchAll(/"text":"(?<textContent>[^"]+)?"/g)
    .some((it) => it.groups?.textContent.trim());

  return !containsImage && !containsText;
}

export function getScrollBehavior(): ScrollBehavior {
  // Use smooth scrolling only if user does not dislike animations
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ? "instant"
    : "smooth";
}

export function scrollToHash(hash: string) {
  const hashEl = document.querySelector(
    "#" + CSS.escape(hash.substring(1))
  ) as HTMLElement;
  if (hashEl) {
    // force the focus on the element to make the focus order make sense when user starts tabbing
    const initalTabIndex = hashEl.getAttribute("tabindex");
    hashEl.setAttribute("tabindex", "-1");
    hashEl.focus();
    initalTabIndex
      ? hashEl.setAttribute("tabindex", initalTabIndex)
      : hashEl.removeAttribute("tabindex");
    hashEl.scrollIntoView();
  }
}

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Creates a File object from a given URL
 *
 * @returns {Promise<File | null>} the created File or null if any error
 */
export async function createFileFromUrl(url: string): Promise<File | null> {
  let mimeType: string | undefined = undefined;
  return await fetch(url)
    .then((res: Response) => {
      mimeType = res.headers.get("content-type") || undefined;
      return res.arrayBuffer();
    })
    .then((buf: ArrayBuffer) => {
      return new File([buf], "external", { type: mimeType });
    });
}

export function isImage(file: File | FileListFile) {
  if (file instanceof File) {
    return file.type.startsWith("image");
  } else if ("mimetype" in file) {
    return file.mimetype.startsWith("image");
  }
}
